// src/SupportAdmin.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Account, Databases, ID, Permission, Query, Role } from "appwrite";
import { toast } from "react-toastify";
import client from "./appwriteConfig";
import "./css/Support.css";
const database = new Databases(client);
const account = new Account(client);
const DATABASE_ID = process.env.REACT_APP_DATABASE_ID;

const MESSAGES_ID = process.env.REACT_APP_SUPPORT_MESSAGES_COLLECTION_ID;

const ADMIN_EMAIL = process.env.REACT_APP_SUPPORT_ADMIN_EMAIL;
const ADMIN_USER_ID = process.env.REACT_APP_SUPPORT_ADMIN_USER_ID;
const formatMessageTime = (dateValue) => {
  if (!dateValue) return "";

  return new Date(dateValue).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};
const addUniqueMessage = (currentMessages, newMessage) => {
  if (currentMessages.some((message) => message.$id === newMessage.$id)) {
    return currentMessages;
  }

  return [...currentMessages, newMessage].sort(
    (a, b) => new Date(a.$createdAt) - new Date(b.$createdAt),
  );
};

function SupportAdmin() {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [authorized, setAuthorized] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    verifyAdmin();
  }, []);

  useEffect(() => {
    if (!authorized) return undefined;

    const unsubscribeTickets = client.subscribe(
      `databases.${DATABASE_ID}.collections.${MESSAGES_ID}.documents`,
      () => {
        fetchTickets();
      },
    );

    const unsubscribeMessages = client.subscribe(
      `databases.${DATABASE_ID}.collections.${MESSAGES_ID}.documents`,
      (response) => {
        const realtimeMessage = response.payload;

        if (
          response.events.some((event) => event.endsWith(".create")) &&
          realtimeMessage.senderType === "user"
        ) {
          toast.info(`New support message from ${realtimeMessage.senderEmail}`);
        }

        if (
          selectedTicket?.ticketId &&
          realtimeMessage.ticketId === selectedTicket.ticketId &&
          response.events.some((event) => event.endsWith(".create"))
        ) {
          setMessages((previousMessages) =>
            addUniqueMessage(previousMessages, realtimeMessage),
          );
        }

        fetchTickets();
      },
    );

    return () => {
      unsubscribeTickets();
      unsubscribeMessages();
    };
  }, [authorized, selectedTicket?.ticketId]);
  const buildTicketSummaries = (documents) => {
    const groupedTickets = new Map();

    documents.forEach((document) => {
      if (!document.ticketId || groupedTickets.has(document.ticketId)) return;

      groupedTickets.set(document.ticketId, {
        ...document,
        $id: document.ticketId,
        latestMessageDocumentId: document.$id,
        lastMessage: document.message,
        lastSender: document.senderType,
        status: document.status || "open",
        unreadAdmin: Boolean(document.unreadAdmin),
        unreadUser: Boolean(document.unreadUser),
      });
    });

    return Array.from(groupedTickets.values());
  };
  const verifyAdmin = async () => {
    try {
      const currentUser = await account.get();
      const isAdmin =
        currentUser.email?.toLowerCase() === ADMIN_EMAIL?.toLowerCase();

      setAdmin(currentUser);
      setAuthorized(isAdmin);

      if (isAdmin) {
        await fetchTickets();
      }
    } catch (error) {
      setAuthorized(false);
    }
  };

  const fetchTickets = async () => {
    try {
      const response = await database.listDocuments(DATABASE_ID, MESSAGES_ID, [
        Query.orderDesc("$updatedAt"),
        Query.limit(100),
      ]);

      const ticketSummaries = buildTicketSummaries(response.documents);

      setTickets(ticketSummaries);

      setSelectedTicket((currentSelectedTicket) => {
        if (!currentSelectedTicket) return null;

        return (
          ticketSummaries.find(
            (ticket) => ticket.ticketId === currentSelectedTicket.ticketId,
          ) || currentSelectedTicket
        );
      });
    } catch (error) {
      console.error("Could not load support tickets:", error);
    }
  };

  const openTicket = async (ticket) => {
    setSelectedTicket(ticket);

    try {
      const response = await database.listDocuments(DATABASE_ID, MESSAGES_ID, [
        Query.equal("ticketId", ticket.ticketId),
        Query.orderAsc("$createdAt"),
        Query.limit(200),
      ]);

      setMessages(response.documents);

      if (ticket.unreadAdmin) {
        await database.updateDocument(DATABASE_ID, MESSAGES_ID, ticket.$id, {
          unreadAdmin: false,
        });
      }
    } catch (error) {
      console.error("Could not open support ticket:", error);
    }
  };

  const getAdminMessagePermissions = (ownerId) => [
    Permission.read(Role.user(ownerId)),
    Permission.update(Role.user(ownerId)),

    Permission.read(Role.user(ADMIN_USER_ID)),
    Permission.update(Role.user(ADMIN_USER_ID)),
    Permission.delete(Role.user(ADMIN_USER_ID)),
  ];
  const sendReply = async () => {
    const cleanedReply = reply.trim();

    if (!cleanedReply || !selectedTicket || !admin || sending) return;

    setSending(true);

    try {
      const now = new Date().toISOString();

      const createdReply = await database.createDocument(
        DATABASE_ID,
        MESSAGES_ID,
        ID.unique(),
        {
          ticketId: selectedTicket.ticketId,

          ownerId: selectedTicket.ownerId,
          ownerEmail: selectedTicket.ownerEmail,
          ownerName:
            selectedTicket.ownerName || selectedTicket.ownerEmail || "User",

          subject: selectedTicket.subject || "Support request",
          pageUrl: selectedTicket.pageUrl || "",

          status: "answered",
          unreadAdmin: false,
          unreadUser: true,

          senderId: admin.$id,
          senderEmail: admin.email,
          senderType: "admin",
          message: cleanedReply,
        },
        getAdminMessagePermissions(selectedTicket.ownerId),
      );

      setMessages((previousMessages) =>
        addUniqueMessage(previousMessages, createdReply),
      );

      setReply("");

      await fetchTickets();
    } catch (error) {
      console.error("Could not send admin reply:", error);
      toast.error(error.message || "Unable to send reply.");
    } finally {
      setSending(false);
    }
  };

  const markResolved = async () => {
    if (!selectedTicket) return;

    try {
      const latestDocumentId =
        messages[messages.length - 1]?.$id ||
        selectedTicket.latestMessageDocumentId;

      await database.updateDocument(
        DATABASE_ID,
        MESSAGES_ID,
        latestDocumentId,
        {
          status: "resolved",
          unreadAdmin: false,
        },
      );

      toast.success("Ticket marked as resolved");
      await fetchTickets();
    } catch (error) {
      toast.error(error.message || "Unable to resolve ticket.");
    }
  };

  if (authorized === null) {
    return <div className="support-admin-status">Checking admin access...</div>;
  }

  if (!authorized) {
    return (
      <div className="support-admin-status">
        <h2>Access denied</h2>
        <p>
          Log in using <strong>{ADMIN_EMAIL}</strong> to access support tickets.
        </p>
      </div>
    );
  }

  return (
    <main className="support-admin-page">
      <header className="support-admin-heading">
        <div>
          <h3>Support Admin</h3>
          <p>Logged in as {admin.email}</p>
        </div>

        <div className="support-admin-actions">
          <button
            className="support-ai-upload-btn"
            onClick={() => navigate("/admin/aiupload")}
          >
            AI Upload
          </button>

          <span className="support-live-indicator">● Live</span>
        </div>
      </header>

      <section className="support-admin-layout">
        <aside className="support-ticket-list">
          <h1>Tickets</h1>

          {tickets.length === 0 && <p>No support tickets yet.</p>}

          {tickets.map((ticket) => (
            <button
              key={ticket.$id}
              className={`support-ticket-card ${
                selectedTicket?.$id === ticket.$id
                  ? "support-ticket-selected"
                  : ""
              }`}
              onClick={() => openTicket(ticket)}
            >
              <div className="support-ticket-title">
                <strong>{ticket.ownerName || ticket.ownerEmail}</strong>

                {ticket.unreadAdmin && (
                  <span className="support-ticket-new">New</span>
                )}
              </div>

              <p>{ticket.lastMessage}</p>

              <div className="support-ticket-meta">
                <span>{ticket.status}</span>
                <span>{new Date(ticket.$updatedAt).toLocaleString()}</span>
              </div>
            </button>
          ))}
        </aside>

        <section className="support-admin-conversation">
          {!selectedTicket ? (
            <div className="support-admin-empty">
              Select a support ticket to view the conversation.
            </div>
          ) : (
            <>
              <header className="support-conversation-header">
                <div>
                  <h2>{selectedTicket.ownerName}</h2>
                  <p>{selectedTicket.ownerEmail}</p>
                  <small>{selectedTicket.pageUrl}</small>
                </div>

                <button onClick={markResolved}>Mark Resolved</button>
              </header>

              <div className="support-admin-messages">
                {messages.map((supportMessage) => (
                  <div
                    key={supportMessage.$id}
                    className={`support-message ${
                      supportMessage.senderType === "admin"
                        ? "support-message-user"
                        : "support-message-admin"
                    }`}
                  >
                    <small className="support-message-meta-line">
                      <span>
                        {supportMessage.senderType === "admin"
                          ? "Admin"
                          : supportMessage.senderEmail}
                      </span>

                      <span className="support-message-time">
                        {formatMessageTime(supportMessage.$createdAt)}
                      </span>
                    </small>

                    <p>{supportMessage.message}</p>
                  </div>
                ))}
              </div>

              <footer className="support-admin-reply">
                <textarea
                  value={reply}
                  placeholder="Type your reply..."
                  onChange={(event) => setReply(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && !event.shiftKey) {
                      event.preventDefault();
                      sendReply();
                    }
                  }}
                />

                <button onClick={sendReply} disabled={sending || !reply.trim()}>
                  {sending ? "Sending..." : "Send Reply"}
                </button>
              </footer>
            </>
          )}
        </section>
      </section>
    </main>
  );
}

export default SupportAdmin;
